import { createReducer } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import findKey from 'lodash/findKey'

import { userLogout } from '@/redux/auth/actions'
import { ISingleScheduleTemplate } from '@/redux/scheduleTemplates/types'
import { StScheduledExercise } from '@/types'
import {
  addScheduleTemplateFailed,
  addScheduleTemplateRequest,
  addScheduleTemplateSuccess,
  addStScheduledExerciseFailed,
  addStScheduledExerciseRequest,
  addStScheduledExerciseSuccess,
  deleteStScheduledExercisesByIdsFailed,
  deleteStScheduledExercisesByIdsRequest,
  deleteStScheduledExercisesByIdsSuccess,
  editScheduleTemplateFailed,
  editScheduleTemplateRequest,
  editScheduleTemplateSuccess,
  editStScheduledExercisesByIdsFailed,
  editStScheduledExercisesByIdsRequest,
  editStScheduledExercisesByIdsSuccess,
  editStScheduledExerciseSettingsByIdsFailed,
  editStScheduledExerciseSettingsByIdsRequest,
  editStScheduledExerciseSettingsByIdsSuccess,
  getPartialSingleScheduleTemplateSuccess,
  getScheduleTemplatesFailed,
  getScheduleTemplatesRequest,
  getScheduleTemplatesSuccess,
  getSingleScheduleTemplateFailed,
  getSingleScheduleTemplateRequest,
  getSingleScheduleTemplateSuccess,
  removeScheduleTemplateFailed,
  removeScheduleTemplateRequest,
  removeScheduleTemplateSuccess,
  resetScheduleTemplates
} from './actions'
import { ScheduleTemplateModel } from './types'

interface IGetScheduleTemplatesState {
  loading: boolean
  scheduledExercisesSettingsLoading: boolean
  scheduledExercisesLoading: boolean
  editing: boolean
  scheduleTemplatesData: ISingleScheduleTemplate[]
}

const initialState: IGetScheduleTemplatesState = {
  loading: false,
  scheduledExercisesSettingsLoading: false,
  scheduledExercisesLoading: false,
  editing: false,
  scheduleTemplatesData: []
}

interface IAddScheduleTemplateState {
  loading: boolean
}

const initialStateAddScheduleTemplate: IAddScheduleTemplateState = {
  loading: false
}

function getScheduleTemplateKey (
  scheduleTemplates: ScheduleTemplateModel[],
  scheduleTemplateId: number
) {
  const key = findKey(
    scheduleTemplates,
    (scheduleTemplate: ScheduleTemplateModel) =>
      scheduleTemplateId === scheduleTemplate.id
  )

  if (!key) return -1

  return parseInt(key)
}

const scheduleTemplatesReducer = createReducer(initialState, builder => {
  builder
    .addCase(getScheduleTemplatesRequest, state => {
      state.loading = true
    })
    .addCase(getScheduleTemplatesSuccess, (state, action) => {
      state.loading = false
      state.scheduleTemplatesData = action.payload.scheduleTemplatesData
    })
    .addCase(getScheduleTemplatesFailed, state => {
      state.loading = false
      state.scheduleTemplatesData = []
    })
    .addCase(resetScheduleTemplates, state => {
      state.loading = false
      state.scheduleTemplatesData = []
    })
    .addCase(removeScheduleTemplateRequest, state => {
      state.loading = true
    })
    .addCase(removeScheduleTemplateSuccess, (state, action) => {
      state.loading = false
      state.scheduleTemplatesData = state.scheduleTemplatesData.filter(
        scheduleTemplate => scheduleTemplate.id !== action.payload.id
      )
    })
    .addCase(removeScheduleTemplateFailed, state => {
      state.loading = false
    })
    .addCase(getSingleScheduleTemplateRequest, state => {
      state.loading = true
    })
    .addCase(getSingleScheduleTemplateSuccess, (state, action) => {
      const scheduleTemplateKey = getScheduleTemplateKey(
        state.scheduleTemplatesData,
        action.payload.singleScheduleTemplateData.id
      )

      state.loading = false

      if (scheduleTemplateKey === -1) {
        state.scheduleTemplatesData = [
          ...state.scheduleTemplatesData,
          action.payload.singleScheduleTemplateData
        ]

        return
      }

      state.scheduleTemplatesData[scheduleTemplateKey] =
        action.payload.singleScheduleTemplateData
    })
    .addCase(getPartialSingleScheduleTemplateSuccess, (state, action) => {
      if (!action.payload.singleScheduleTemplateData.id) return

      const scheduleTemplateKey = getScheduleTemplateKey(
        state.scheduleTemplatesData,
        action.payload.singleScheduleTemplateData.id
      )

      state.loading = false

      if (scheduleTemplateKey === -1) return

      state.scheduleTemplatesData[scheduleTemplateKey] = {
        ...state.scheduleTemplatesData[scheduleTemplateKey],
        ...action.payload.singleScheduleTemplateData
      }
    })
    .addCase(getSingleScheduleTemplateFailed, state => {
      state.loading = false
    })
    .addCase(editScheduleTemplateRequest, state => {
      state.editing = true
    })
    .addCase(editScheduleTemplateSuccess, (state, action) => {
      const scheduleTemplateKey = getScheduleTemplateKey(
        state.scheduleTemplatesData,
        action.payload.id
      )

      if (scheduleTemplateKey === -1) return

      state.scheduleTemplatesData[scheduleTemplateKey] = action.payload
      state.editing = false
    })
    .addCase(editScheduleTemplateFailed, state => {
      state.editing = false
    })
    .addCase(deleteStScheduledExercisesByIdsRequest, state => {
      state.scheduledExercisesLoading = true
    })
    .addCase(deleteStScheduledExercisesByIdsSuccess, (state, action) => {
      const { stScheduledExerciseIds, scheduleId } = action.payload

      const scheduleKey = getScheduleTemplateKey(
        state.scheduleTemplatesData,
        scheduleId
      )
      if (scheduleKey === -1) return

      let clonedExercises = cloneDeep(
        state.scheduleTemplatesData[scheduleKey].exercises
      )

      clonedExercises = clonedExercises.filter(
        scheduledExercises =>
          !stScheduledExerciseIds.includes(scheduledExercises.dbId)
      )

      state.scheduleTemplatesData[scheduleKey].exercises = clonedExercises

      state.scheduledExercisesLoading = false
    })
    .addCase(deleteStScheduledExercisesByIdsFailed, state => {
      state.scheduledExercisesLoading = false
    })
    .addCase(editStScheduledExercisesByIdsFailed, state => {
      state.scheduledExercisesLoading = false
    })
    .addCase(editStScheduledExercisesByIdsRequest, state => {
      state.scheduledExercisesLoading = true
    })
    .addCase(editStScheduledExercisesByIdsSuccess, (state, action) => {
      const { stScheduledExercisesData, scheduleId } = action.payload

      const scheduleKey = getScheduleTemplateKey(state.scheduleTemplatesData, scheduleId)
      if (scheduleKey === -1) return

      const clonedScheduleExercises = cloneDeep(
        state.scheduleTemplatesData[scheduleKey].exercises
      )

      stScheduledExercisesData.forEach(({ id, ...stScheduledExerciseData }) => {
        const scheduledExerciseKey = findKey(
          state.scheduleTemplatesData[scheduleKey]?.exercises,
          (ex: StScheduledExercise) => ex.dbId === id
        )
        if (!scheduledExerciseKey) return

        clonedScheduleExercises[parseInt(scheduledExerciseKey)] = {
          ...clonedScheduleExercises[parseInt(scheduledExerciseKey)],
          ...stScheduledExerciseData
        }
      })

      state.scheduleTemplatesData[scheduleKey].exercises = clonedScheduleExercises
      state.scheduledExercisesLoading = false
    })
    .addCase(addStScheduledExerciseRequest, state => {
      state.scheduledExercisesLoading = true
    })
    .addCase(addStScheduledExerciseSuccess, (state, action) => {
      const { scheduleId, stScheduledExercise } = action.payload

      const scheduleKey = getScheduleTemplateKey(state.scheduleTemplatesData, scheduleId)
      if (scheduleKey === -1) return

      state.scheduleTemplatesData[scheduleKey].exercises = [
        ...state.scheduleTemplatesData[scheduleKey].exercises,
        ...stScheduledExercise
      ]

      state.scheduledExercisesLoading = false
    })
    .addCase(addStScheduledExerciseFailed, state => {
      state.scheduledExercisesLoading = false
    })
    .addCase(editStScheduledExerciseSettingsByIdsRequest, state => {
      state.scheduledExercisesSettingsLoading = true
    })
    .addCase(editStScheduledExerciseSettingsByIdsFailed, state => {
      state.scheduledExercisesSettingsLoading = false
    })
    .addCase(editStScheduledExerciseSettingsByIdsSuccess, (state, action) => {
      const { value, textId, exercisesIds, templateId } = action.payload

      const scheduleKey = getScheduleTemplateKey(state.scheduleTemplatesData, templateId)
      if (scheduleKey === -1) return

      const clonedSchedulesState = cloneDeep(state.scheduleTemplatesData)

      exercisesIds.forEach(id => {
        const scheduledExerciseKey = findKey(
          state.scheduleTemplatesData[scheduleKey]?.exercises,
          (ex: StScheduledExercise) => ex.dbId === id
        )
        if (!scheduledExerciseKey) return

        const scheduledExerciseSettingKey = findKey(
          state.scheduleTemplatesData[scheduleKey]?.exercises?.[
            parseInt(scheduledExerciseKey)
          ]?.settings,
          (item: any) => item.textId === textId
        )
        if (!scheduledExerciseSettingKey) return

        clonedSchedulesState[scheduleKey].exercises[
          parseInt(scheduledExerciseKey)
        ].settings[parseInt(scheduledExerciseSettingKey)].value = value
      })

      state.scheduleTemplatesData = clonedSchedulesState
      state.scheduledExercisesSettingsLoading = false
    })
    .addCase(userLogout, state => {
      state.loading = false
      state.scheduledExercisesSettingsLoading = false
      state.scheduledExercisesLoading = false
      state.editing = false
      state.scheduleTemplatesData = []
    })
})

const addScheduleTemplateReducer = createReducer(
  initialStateAddScheduleTemplate,
  builder => {
    builder
      .addCase(addScheduleTemplateRequest, state => {
        state.loading = true
      })
      .addCase(addScheduleTemplateSuccess, state => {
        state.loading = false
      })
      .addCase(addScheduleTemplateFailed, state => {
        state.loading = false
      })
  }
)

export { addScheduleTemplateReducer, scheduleTemplatesReducer }
